import React, { useEffect, useRef, useState } from "react";
import AnimatedFeedbackPopup from "../AnimatedFeedbackPopup";

// ✅ UPDATED: support both Object6 & Obect6
const CLICKABLE_OBJECTS = [
  "Object1",
  "Object2",
  "Object3",
  "Object4",
  "Object5",
  ["Object6", "Obect6"], // 👈 important fix
];

const DEFAULT_HIDDEN = [
  "Pers1", "Pers2", "Pers3", "Pers4", "Pers5", "Pers6",
  "Semi_Clean_1", "Semi_Clean_2", "Clean_Pool","Clean","Semi_Clean2"
];

const PERSON_VISIBLE_MS = 1000;
const FADE_MS = 420;
const POINTS_PER_CLICK = 5;

const PERS_BY_OBJECT = {
  Object1: "Pers1",
  Object2: "Pers2",
  Object3: "Pers3",
  Object4: "Pers4",
  Object5: "Pers5",
  Object6: "Pers6", // 👈 keep correct key
};

// ✅ SHOW
function show(node) {
  if (!node) return;

  node.removeAttribute("display");
  node.style.display = "block";
  node.style.visibility = "visible";
  node.style.opacity = "1";
  node.style.pointerEvents = "auto";

  if (node.parentNode) {
    node.parentNode.appendChild(node);
  }
}

// ✅ HIDE
function hide(node) {
  if (!node) return;

  node.setAttribute("display", "none");
  node.style.display = "none";
  node.style.visibility = "hidden";
  node.style.opacity = "0";
  node.style.pointerEvents = "none";
}

function transitionHide(node, timersRef, delayMs = 0) {
  if (!node) return;

  node.style.pointerEvents = "none";
  node.style.transition = `opacity ${FADE_MS}ms ease`;

  const fadeTimer = setTimeout(() => {
    node.style.opacity = "0";
    const endTimer = setTimeout(() => hide(node), FADE_MS);
    timersRef.current.push(endTimer);
  }, delayMs);

  timersRef.current.push(fadeTimer);
}

function transitionShow(node) {
  if (!node) return;

  node.removeAttribute("display");
  node.style.display = "block";
  node.style.visibility = "visible";
  node.style.pointerEvents = "auto";
  node.style.opacity = "0";
  node.style.position = "relative";
  node.style.zIndex = "1000";
  node.style.transition = `opacity ${FADE_MS}ms ease`;

  const svgRoot = node.ownerSVGElement;
  if (svgRoot) {
    svgRoot.appendChild(node);
  } else if (node.parentNode) {
    node.parentNode.appendChild(node);
  }

  requestAnimationFrame(() => {
    node.style.opacity = "1";
  });
}

function firstExistingNode(svgDoc, ids = []) {
  for (const id of ids) {
    const node = svgDoc.getElementById(id);
    if (node) return node;
  }
  return null;
}

export default function SwimmingPoolStage({ data, onDone, onSessionPointsChange }) {
  const objectRef = useRef(null);
  const cleanupRef = useRef(null);
  const timersRef = useRef([]);
  const doneRef = useRef(false);
  const popupTimerRef = useRef(null);

  const [sessionPoints, setSessionPoints] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    onSessionPointsChange?.(sessionPoints);
  }, [sessionPoints]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const objectEl = objectRef.current;
    if (!objectEl || !data.svgAsset) return;

    const handleLoad = () => {
      const svgDoc = objectEl.contentDocument;
      if (!svgDoc) return;

      const clicked = new Set();
      const cleaners = [];
      let activeClickableCount = 0;

      // initial hide
      DEFAULT_HIDDEN.forEach((id) => hide(svgDoc.getElementById(id)));

      // ✅ UPDATED LOOP (handles multiple IDs)
      CLICKABLE_OBJECTS.forEach((objectIdRaw) => {
        const ids = Array.isArray(objectIdRaw) ? objectIdRaw : [objectIdRaw];

        let objectNode = null;
        let actualId = null;

        for (let id of ids) {
          const node = svgDoc.getElementById(id);
          if (node) {
            objectNode = node;
            actualId = id;
            break;
          }
        }

        if (!objectNode) return;
        activeClickableCount += 1;

        objectNode.style.cursor = "pointer";
        objectNode.style.pointerEvents = "auto";

        const onClick = () => {
          if (clicked.has(actualId)) return;
          clicked.add(actualId);

          setSessionPoints((p) => p + POINTS_PER_CLICK);
          objectNode.style.pointerEvents = "none";

          // popup
          if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
          setPopupOpen(true);
          popupTimerRef.current = setTimeout(() => setPopupOpen(false), 900);

          // ✅ FIX mapping for typo case
          const personKey = actualId === "Obect6" ? "Object6" : actualId;
          const person = svgDoc.getElementById(PERS_BY_OBJECT[personKey]);

          transitionShow(person);
          transitionHide(person, timersRef, PERSON_VISIBLE_MS);

          transitionHide(objectNode, timersRef, PERSON_VISIBLE_MS);

          // semi clean
          if (actualId === "Object2") {
            hide(firstExistingNode(svgDoc, ["Dirty1", "Dirt1"]));
            show(firstExistingNode(svgDoc, ["Semi_Clean_2", "Semi_Clean2"]));
          }

          if (actualId === "Object4") {
            hide(firstExistingNode(svgDoc, ["Dirty2", "Dirt2"]));
            show(firstExistingNode(svgDoc, ["Semi_Clean_1", "Semi_Clean1"]));
          }

          // ✅ FINAL STATE
          if (clicked.size === activeClickableCount && !doneRef.current) {
            doneRef.current = true;

            const dirty1 = firstExistingNode(svgDoc, ["Dirty1", "Dirt1"]);
            const dirty2 = firstExistingNode(svgDoc, ["Dirty2", "Dirt2"]);
            const semi1 = firstExistingNode(svgDoc, ["Semi_Clean_1", "Semi_Clean1"]);
            const semi2 = firstExistingNode(svgDoc, ["Semi_Clean_2", "Semi_Clean2"]);
            const cleanPool = firstExistingNode(svgDoc, ["Clean_Pool", "Clean"]);

            [dirty1, dirty2, semi1, semi2].forEach((el) => {
              if (!el) return;
              el.style.transition = "none";
              el.style.opacity = "0";
              el.setAttribute("display", "none");
              el.style.display = "none";
              el.style.visibility = "hidden";
            });

            if (cleanPool) {
              cleanPool.style.transition = "none";
              cleanPool.removeAttribute("display");
              cleanPool.style.display = "block";
              cleanPool.style.visibility = "visible";
              cleanPool.style.opacity = "1";

              if (cleanPool.parentNode) {
                cleanPool.parentNode.appendChild(cleanPool);
              }
            }

            // Keep the just-clicked person above final-state pool layers on the last click.
            if (person) {
              const svgRoot = person.ownerSVGElement;
              if (svgRoot) {
                svgRoot.appendChild(person);
              } else if (person.parentNode) {
                person.parentNode.appendChild(person);
              }
            }

            setTimeout(() => {
              onDone(clicked.size * POINTS_PER_CLICK);
            }, 900);
          }
        };

        objectNode.addEventListener("click", onClick);
        cleaners.push(() => objectNode.removeEventListener("click", onClick));
      });

      cleanupRef.current = () => cleaners.forEach((fn) => fn());
    };

    objectEl.addEventListener("load", handleLoad);

    if (objectEl.contentDocument?.readyState === "complete") {
      handleLoad();
    }

    return () => {
      objectEl.removeEventListener("load", handleLoad);
      cleanupRef.current?.();
    };
  }, [data.svgAsset]);

  return (
    <div className="swimming-stage swimming-stage--bleed">
      <div className="swimming-stage__svg-wrap">
        <object
          ref={objectRef}
          data={data.svgAsset}
          type="image/svg+xml"
          className="swimming-stage__object"
        />
      </div>

      <AnimatedFeedbackPopup open={popupOpen} type="success" title="Awesome!" />
    </div>
  );
}